export const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;      // 10 MB
export const TARGET_SIZE = 3 * 1024 * 1024;           // 3 MB
export const MAX_DIMENSION = 1920;

/**
 * Compresses an image file to WebP format with a target size.
 * Returns a Promise<File> with the compressed image.
 */
export async function compressImage(file: File): Promise<File> {
  // If file is already under target size, return as-is (optional)
  if (file.size <= TARGET_SIZE) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Resize
        let width = img.width;
        let height = img.height;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);

        // Start with quality 0.82
        let quality = 0.82;
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Canvas toBlob failed'));
                return;
              }
              if (blob.size <= TARGET_SIZE) {
                // Good enough
                const newFile = new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), {
                  type: 'image/webp',
                });
                resolve(newFile);
              } else if (quality > 0.3) {
                // Lower quality and try again
                quality -= 0.1;
                tryCompress();
              } else {
                // Still too large – reject
                reject(new Error('Unable to compress image to target size.'));
              }
            },
            'image/webp',
            quality,
          );
        };
        tryCompress();
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}